// import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
// import CancelIcon from '@mui/icons-material/Cancel';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha, styled, useTheme as useMuiTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import ExportCsvButton from "../../components/ExportCsvButton";
import { useTransactions, useExportTransactions, useGenerateReceipt } from "../../hooks/useTransactions";
import TableSkeleton from "../../components/TableSkeleton";

// Styled components
const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  // backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let bg, color;

  // Check if we're in light mode
  const isLightMode = theme.palette.mode === "light";

  switch (status?.toLowerCase()) {
    case "completed":
    case "success":
      bg = isLightMode
        ? "transparent"
        : alpha(theme.palette.success.main, 0.25);
      color = isLightMode
        ? theme.palette.success.main
        : theme.palette.success.main;
      break;
    case "failed":
      bg = isLightMode ? "transparent" : alpha(theme.palette.error.main, 0.2);
      color = isLightMode ? theme.palette.error.main : theme.palette.error.main;
      break;
    case "pending":
      bg = isLightMode ? "transparent" : alpha(theme.palette.warning.main, 0.2);
      color = isLightMode
        ? theme.palette.warning.main
        : theme.palette.warning.main;
      break;
    case "expired":
      bg = isLightMode ? "transparent" : alpha(theme.palette.info.main, 0.2);
      color = isLightMode ? theme.palette.info.main : theme.palette.info.main;
      break;
    case "refunded":
      bg = isLightMode ? "transparent" : alpha(theme.palette.secondary.main, 0.2);
      color = isLightMode ? theme.palette.secondary.main : theme.palette.secondary.main;
      break;
    case "cancelled":
      bg = isLightMode ? "transparent" : alpha(theme.palette.grey[500], 0.2);
      color = isLightMode ? theme.palette.grey[700] : theme.palette.grey[300];
      break;
    default:
      bg = isLightMode ? "transparent" : alpha(theme.palette.text.primary, 0.2);
      color = isLightMode
        ? theme.palette.text.primary
        : theme.palette.text.primary;
  }
  return {
    backgroundColor: bg,
    color,
    borderRadius: 4,
    height: 24,
    fontSize: "0.80rem",
  };
});
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.action.disabledBackground, 0.1),
  width: "100%",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
}));

const StyledInputBase = styled(TextField)(({ theme }) => ({
  color: theme.palette.text.primary,
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
  },
}));

const TimeFilterButton = styled(Button)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.primary.main
    : alpha(theme.palette.action.disabledBackground, 0.2),
  color: active ? theme.palette.common.white : theme.palette.text.primary,
  borderRadius: 20,
  textTransform: "none",
  padding: "4px 16px",
  fontWeight: "bold",
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.dark
      : alpha(theme.palette.action.disabledBackground, 0.3),
  },
}));

// Updated mock data with custom images

const Transaction = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [tabValue, setTabValue] = useState(0);
  const [timeFilter, setTimeFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterConfig, setFilterConfig] = useState({
    "Transaction ID": true,
    User: true,
    Date: true,
    Amount: true,
    "Payment Method": true,
    Status: true,
    Receipt: true,
    Revenue: true,
  });

  // Map tab values to API status filters
  const tabValueMap = {
    0: null, // All
    1: "completed", // Successful
    2: "pending", // Pending
    3: "failed", // Failed
    4: "expired", // Expired
  };

  // Fetch transactions with current filters
  const { data: transactions = {}, isLoading, error } = useTransactions({
    page,
    limit: rowsPerPage,
    status: tabValueMap[tabValue],
    search: searchTerm,
    dateFilter: timeFilter.toLowerCase(),
  });

  const exportMutation = useExportTransactions();

  const theme = useMuiTheme();
  
  const visibleColumns = {
    User: true,
    Date: true,
    Amount: true,
    "Payment Method": true,
    Status: true,
    Receipt: true,
    Revenue: true,
  };

  const handleTabChange = (_, v) => {
    setTabValue(v);
    setPage(1); // Reset to page 1 when tab changes
  };

  const handleRowsChange = (e) => setRowsPerPage(e.target.value);
  const handleMoreClick = (e, row) => {
    setCurrentRow(row);
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  const generateReceiptMutation = useGenerateReceipt();

  const handleGenerateReceipt = async (row) => {
    try {
      setAnchorEl(null);
      const data = await generateReceiptMutation.mutateAsync(row._id || row.id);
      setReceiptData(data);
      setReceiptDialogOpen(true);
    } catch (error) {
      console.error("Failed to generate receipt:", error);
    }
  };

  const handleDownloadReceipt = async (row) => {
    try {
      setAnchorEl(null);
      const data = await generateReceiptMutation.mutateAsync(row._id || row.id);
      
      // Create a formatted text content for the receipt
      const content = `
TRANSACTION RECEIPT
-------------------
Transaction ID: ${data.transactionId || 'N/A'}
Date: ${new Date(data.date).toLocaleString()}
Status: ${data.status}

Product: ${data.productName || 'N/A'}
Plan: ${data.planName || 'N/A'}
Description: ${data.description || 'N/A'}

Amount: ${data.currency} ${data.amount}
Payment Method: ${data.paymentMethod}
Card: ${data.card?.brand} **** ${data.card?.last4}

User: ${data.user?.name || 'N/A'}
Email: ${data.user?.email || 'N/A'}
-------------------
Thank you for your business!
      `;

      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${data.transactionId || 'transaction'}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download receipt:", error);
    }
  };
  const handleTimeChange = (f) => {
    setTimeFilter(f);
    setPage(1); // Reset to page 1 when time filter changes
  };
  const handlePageChange = (_, v) => setPage(v);
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to page 1 when search changes
  };

  // Filter dialog handlers - updated to match the first code
  const handleFilterDialogOpen = () => setFilterDialogOpen(true);
  const handleFilterDialogClose = () => setFilterDialogOpen(false);

  const handleFilterChange = (key) => {
    setFilterConfig((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Get filtered transactions from API
  const getFilteredTransactions = () => {
    console.log("Transactions data:", transactions);
    return transactions.data || [];
  };

  // Get export parameters
  const getExportParams = () => {
    return {
      status: tabValueMap[tabValue],
      search: searchTerm,
      dateFilter: timeFilter.toLowerCase(),
    };
  };

  const filtered = getFilteredTransactions();
  const totalPages = transactions.pagination?.pages || 1;

  // Generate table headers based on filterConfig
  const getTableHeaders = () => {
    const headerMapping = {
      "Transaction ID": "ID",
      User: "User",
      Date: "Date",
      Amount: "Amount",
      "Payment Method": "Method",
      Status: "Status",
      Receipt: "Receipt",
      // 'Revenue': 'Revenue'
    };

    return Object.entries(filterConfig)
      .filter(([_, enabled]) => enabled)
      .map(([key, _]) => headerMapping[key])
      .filter((header) => header); // Filter out undefined headers
  };

  // Function to render table cell based on header - updated for new API structure
  const renderTableCell = (row, header) => {
    switch (header) {
      case "ID":
        return <TableCell key={header}>
          {row.transactionId || (row._id ? `...${row._id.slice(-6)}` : 'N/A')}
        </TableCell>;
      case "User":
        return (
          <TableCell key={header}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar src={row?.user?.avatar} sx={{ width: 24, height: 24 }} />
              {row?.user?.name || 'Unknown User'}
            </Box>
          </TableCell>
        );
      case "Date":
        return (
          <TableCell key={header}>
            <Typography>{new Date(row.paymentDate || row.createdAt).toLocaleDateString()}</Typography>
            <Typography variant="caption" color="text.primary">
              {new Date(row.paymentDate || row.createdAt).toLocaleTimeString()}
            </Typography>
          </TableCell>
        );
      case "Amount":
        return <TableCell key={header}>${row?.amount?.toFixed(2)}</TableCell>;
      case "Method":
        return <TableCell key={header}>{row.paymentMethod}</TableCell>;
      case "Status":
        return (
          <TableCell key={header}>
            <StatusChip
              status={row?.status}
              label={row?.status}
            />
          </TableCell>
        );
      case "Receipt":
        return (
          <TableCell key={header} align="centre">
            <IconButton
              onClick={(e) => handleMoreClick(e, row)}
              sx={{
                color: theme.palette.text.primary,
                padding: "4px",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.action.hover, 0.2),
                },
              }}
              size="small"
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          </TableCell>
        );
      case "Revenue":
        return <TableCell key={header}>-</TableCell>;
      default:
        return <TableCell key={header}></TableCell>;
    }
  };

  // Get headers for display
  const tableHeaders = getTableHeaders();
  const isDarkMode = theme.palette.mode === "dark";

  // Get CSV columns for export based on filterConfig
  const getCsvColumns = () => {
    const keyMapping = {
      "Transaction ID": "id",
      User: "user.name",
      Date: "date",
      Amount: "amount",
      "Payment Method": "paymentMethod",
      Status: "status",
      Receipt: "receipt",
    };

    return Object.entries(filterConfig)
      .filter(([key, enabled]) => enabled && keyMapping[key])
      .map(([key, _]) => ({
        label: key,
        key: keyMapping[key],
      }));
  };

  return (
    <Box
      sx={{
        bgcolor: "transparent",
        color: theme.palette.text.primary,
        // p: 3,
        height: "100vh",
      }}
    >
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Transactions",
            value: transactions.summary?.total || 0,
            icon: (
              <img
                src="/Images/hugeicons_transaction.png"
                alt="IP address"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.primary.main,
          },
          {
            label: "Successful Transactions",
            value: transactions.summary?.successful || 0,
            icon: (
              <img
                src="/Images/mdi_tick-circle-outline.png"
                alt="IP address"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.success.main,
          },
          {
            label: "Pending Transactions",
            value: transactions.summary?.pending || 0,
            icon: (
              <img
                src="/Images/clock.png"
                alt="Pending"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.warning.main,
          },
          {
            label: "Failed Transactions",
            value: transactions.summary?.failed || 0,
            icon: (
              <img
                src="/Images/ix_namur-failure.png"
                alt="IP address"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.error.main,
          },
          {
            label: "Expired Transactions",
            value: transactions.summary?.expired || 0,
            icon: (
              <img
                src="/Images/expired.png"
                alt="Expired"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.info.main,
          },
          {
            label: "Refunded Transactions",
            value: transactions.summary?.refunded || 0,
            icon: (
              <img
                src="/Images/refund.png"
                alt="Refunded"
                style={{ width: 24, height: 24 }}
              />
            ),
            color: theme.palette.secondary.main,
          },
        ].map((stat, i) => (
          <Grid item xs={12} md={4} key={i} >
            <StatsCard sx={{bgcolor: "transparent",                   boxShadow: 'none' ,
                  border: `1px solid ${theme.palette.divider}`,
                  padding: theme.spacing(2),
                  borderRadius: 2,}}>
              <Box>
                <Typography variant="body2" color="text.primary">
                  {stat.label}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stat.value}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: stat.color,
                  width: 40,
                  height: 40,
                  borderRadius: 2, // same 2px rounding
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {stat.icon}
              </Box>
            </StatsCard>
          </Grid>
        ))}
      </Grid>

      {/* Transactions Table */}
      <Box
        sx={{ 
          bgcolor: "transparent",
                            boxShadow: 'none' ,
                  border: `1px solid ${theme.palette.divider}`,
                  padding: theme.spacing(2),
                  borderRadius: 2,
           p: 2 }}
      >
        {/* Header Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mb: 4,
          }}
        >
          {/* Left group: Title + Rows selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.9,
            }}
          >
            <Typography variant="h4">Transactions</Typography>

            <Typography
              sx={{
                fontSize: "14px",
              }}
              color="text.secondary"
            >
              Rows per page:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={handleRowsChange}
              sx={{
                color: theme.palette.text.primary,
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider,
                },
                ".MuiSvgIcon-root": { color: theme.palette.text.secondary },
                minWidth: 65,
                height: 25,
              }}
              IconComponent={KeyboardArrowDownIcon}
            >
              {[6, 10, 25, 50].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Right group: Search + Filter + Export */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Search sx={{ width: 300, height: 40 }}>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Search>

            {/* Filter Icon Button */}

            <Button
              variant="outlined"
              onClick={() => exportMutation.mutate({ format: 'csv', ...getExportParams() })}
              disabled={exportMutation.isLoading}
              sx={{ width: 120 }}
            >
              {exportMutation.isLoading ? 'Exporting...' : 'CSV'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => exportMutation.mutate({ format: 'excel', ...getExportParams() })}
              disabled={exportMutation.isLoading}
              sx={{ width: 120, ml: 1 }}
            >
              {exportMutation.isLoading ? 'Exporting...' : 'Excel'}
            </Button>
            <IconButton
              onClick={handleFilterDialogOpen}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.common.white,
                borderRadius: 1,
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs + Time Filters */}
        <Box
          sx={{
            mt: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {/* Left: Transaction Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            TabIndicatorProps={{ sx: { bgcolor: theme.palette.primary.main } }}
          >
            {["ALL", "SUCCESSFUL", "PENDING", "FAILED", "EXPIRED"].map((label) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  textTransform: "none",
                  color: theme.palette.text.secondary,
                  "&.Mui-selected": { color: theme.palette.primary.main },
                }}
              />
            ))}
          </Tabs>

          {/* Right: Time Filters */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {["All", "Day", "Week", "Month"].map((f) => (
              <TimeFilterButton
                key={f}
                active={timeFilter === f}
                onClick={() => handleTimeChange(f)}
              >
                {f}
              </TimeFilterButton>
            ))}
          </Box>
        </Box>

        {/* Table */}
        <TableContainer       sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(2),
              }}>
          <Table>
            <TableHead>
              <TableRow          sx={{
                      backgroundColor: isDarkMode
                        ? "#171717"
                        : "background.neutral",
                    }}>
                {tableHeaders.map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: "1.2rem",
                      color: theme.palette.text.primary,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      fontWeight: "bold",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length}>
                    <TableSkeleton
                      animation="wave"
                      variant="rectangular"
                      width="100%"
                      height={40}
                      sx={{ bgcolor: alpha(theme.palette.action.hover, 0.1) }}
                    />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} align="center">
                     <Typography color="error">Failed to load transactions. Please try again.</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} align="center">
                     <Typography>No transactions found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row, i) => (
                  <TableRow key={row._id || i} hover>
                    {tableHeaders.map((header) =>
                      renderTableCell(row, header)
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography color="text.secondary">
            Page {page} of {totalPages || 1}
          </Typography>
          <Pagination
            count={totalPages || 1}
            page={page}
            onChange={handlePageChange}
            size="small"
            renderItem={(item) => <PaginationItem {...item} />}
          />
        </Box>
      </Box>

      {/* More menu - Fixed to use theme colors instead of hardcoded black */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            // backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            minWidth: "120px",
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.3)",
            borderRadius: "4px",
          },
        }}
      >
        <MenuItem
          onClick={() => handleGenerateReceipt(currentRow)}
          sx={{
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          View Receipt
        </MenuItem>
        <MenuItem
          onClick={() => handleDownloadReceipt(currentRow)}
          sx={{
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          Download Receipt
        </MenuItem>
      </Menu>

      {/* Filter Dialog - Updated to match the first code */}
      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterDialogClose}
        PaperProps={{
          sx: {
            position: "absolute",
            bottom: 16,
            right: 16,
            width: "350px",
            maxWidth: "90vw",
            // bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: 2,
            m: 0,
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0,0,0,0.3)" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6">Filter Tabs</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleFilterDialogClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {Object.keys(filterConfig).map((key) => (
            <Box key={key}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <Typography variant="body1">{key}</Typography>
                <Switch
                  checked={filterConfig[key]}
                  onChange={() => handleFilterChange(key)}
                  color="primary"
                  size="small"
                />
              </Box>
              <Divider
                sx={{ backgroundColor: alpha(theme.palette.divider, 0.5) }}
              />
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Transaction Receipt</Typography>
          <IconButton onClick={() => setReceiptDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {receiptData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Transaction ID</Typography>
                <Typography fontWeight="bold">{receiptData.transactionId || 'N/A'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Date</Typography>
                <Typography>{new Date(receiptData.date).toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Status</Typography>
                <StatusChip status={receiptData.status} label={receiptData.status} />
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Amount</Typography>
                <Typography variant="h6" color="primary.main">
                  {receiptData.currency} {receiptData.amount}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Payment Method</Typography>
                <Typography sx={{ textTransform: 'capitalize' }}>
                  {receiptData.paymentMethod?.replace('_', ' ')}
                </Typography>
              </Box>
               {receiptData.card && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Card</Typography>
                  <Typography>
                    {receiptData.card.brand} **** {receiptData.card.last4}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Box>
                <Typography color="text.secondary" gutterBottom>Product Details</Typography>
                <Typography variant="subtitle1">{receiptData.productName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Plan: {receiptData.planName}
                </Typography>
                {receiptData.description && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {receiptData.description}
                  </Typography>
                )}
              </Box>
              <Divider />
              <Box>
                 <Typography color="text.secondary" gutterBottom>Customer</Typography>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {receiptData.user?.avatar && <Avatar src={receiptData.user.avatar} sx={{ width: 24, height: 24 }} />}
                    <Typography>{receiptData.user?.name}</Typography>
                 </Box>
                 <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {receiptData.user?.email}
                 </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Transaction;
